"use client";

import { useQuery } from "@tanstack/react-query";
import { CAMPAIGN_CONFIG } from "@/config";
import { PulsarService } from "@/services/pulsar.service";

export interface PortalStats {
  totalUsers: number;
  totalTasksDone: number;
  isLoading: boolean;
  error: Error | null;
}

export const PORTAL_STATS_KEY = ["portal", "stats"] as const;

/**
 * Custom hook to fetch portal statistics
 * Returns total users and total tasks done
 */
export function usePortalStats(): PortalStats {
  const { data, isLoading, error } = useQuery({
    queryKey: PORTAL_STATS_KEY,
    queryFn: () =>
      PulsarService.getCampaignStats(
        CAMPAIGN_CONFIG.id,
        CAMPAIGN_CONFIG.groupId
      ),
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
  });

  return {
    totalUsers: data?.total_users ?? 0,
    totalTasksDone: data?.total_successful_tasks
      ? parseInt(data.total_successful_tasks, 10)
      : 0,
    isLoading,
    error: error as Error | null,
  };
}

/**
 * Format large numbers with K/M suffixes
 */
export function formatPortalNumber(num: number): string {
  if (num >= 1000000) {
    const millions = num / 1000000;
    // If it's a clean number like 2.5M, show one decimal
    // Otherwise show no decimals
    if (millions % 0.5 === 0) {
      return `${millions.toFixed(1)}M`;
    }
    return `${millions.toFixed(1)}M`;
  } else if (num >= 1000) {
    const thousands = num / 1000;
    return `${Math.round(thousands)}K`;
  }
  return num.toString();
}
